import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { BattingRankingStats, DataSection, PitchingRankingStats, RankingScopeContext, ResultDataNew, RunnerSafe, ScoringData } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./../MultiRankingStatCalculator.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import classifiesAsStolenBase from "../../../classificators/runnerSafeType/classifiesAsStolenBase.js";
import classifiesAsCaughtStealing from "../../../classificators/outType/classifiesAsCaughtStealing.js";
import resultsSliceForPlayer from "../../../classificators/resultsSlice/resultsSliceForPlayer.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import classifiesAsRun from "../../../classificators/runnerSafeType/classifiesAsRun.js";
import classifiesAsHomeRun from "../../../classificators/plateAppearanceResult/classifiesAsHomeRun.js";

class NewBaseRunningStatsCalculator extends MultiRankingStatCalculator{
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache,
        stolenBaseClassifier, caughtStealingClassifier, runClassifier, homeRunClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(stolenBaseClassifier) !== 'undefined' && typeof(stolenBaseClassifier) !== 'function') {
            throw new TypeError('stolenBaseClassifier must be an function or undefined');
        }
        if (typeof(caughtStealingClassifier) !== 'undefined' && typeof(caughtStealingClassifier) !== 'function') {
            throw new TypeError('caughtStealingClassifier must be an function or undefined');
        }
        if (typeof(runClassifier) !== 'undefined' && typeof(runClassifier) !== 'function') {
            throw new TypeError('runClassifier must be an function or undefined');
        }
        if (typeof(homeRunClassifier) !== 'undefined' && typeof(homeRunClassifier) !== 'function') {
            throw new TypeError('homeRunClassifier must be an function or undefined');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Batting.key
            ? [ BattingRankingStats.Runs, BattingRankingStats.StolenBases, BattingRankingStats.CaughtStealing, ]
            : [ PitchingRankingStats.RunsAllowed, PitchingRankingStats.StolenBasesAllowed, PitchingRankingStats.CaughtStealingAllowed, ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    context === RankingScopeContext.Batting.key ? battingScopeContext: pitchingScopeContext,
                    resultsSliceForPlayer
                )
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.stolenBaseClassifier = stolenBaseClassifier || classifiesAsStolenBase;
        this.caughtStealingClassifier = caughtStealingClassifier || classifiesAsCaughtStealing;
        this.runClassifier = runClassifier || classifiesAsRun;
        this.homeRunClassifier = homeRunClassifier || classifiesAsHomeRun;
        this.context = context;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        // in this case we need to analyze entire session
        const results = slice.getSmallestSlice();

        const output = {};
        let runs = 0;
        let caughtStealing = 0;
        let stolenBases = 0;

        for (let r = 0; r < results.length; r++) {
            const result = results[r];

            if (
                !result
                || !result[ResultDataNew.Data.key]
                || !result[ResultDataNew.Data.key][DataSection.Scoring.key]
            ) {
                continue;
            }

            // check for home run
            const paResult = result[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.PlateAppearanceResult.key];
            const isHomeRun = this.homeRunClassifier(paResult);

            // run through RunnerSafe
            if (result[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.RunnerSafe.key]) {
                const runnersSafe = result[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.RunnerSafe.key] || [];
                for (let rs = 0; rs < runnersSafe.length; rs++) {
                    const runnerAdv = runnersSafe[rs];

                    if (
                        !runnerAdv
                        // for pitching, we will check what pitcher allowed runners to do
                        || (this.context === RankingScopeContext.Pitching.key && !this.hasPitched(result, slice))
                        // for batting context, we will qualify runners
                        || (this.context === RankingScopeContext.Batting.key && runnerAdv[RunnerSafe.RunnerId.key] !== slice.scope.tagId)
                    ) {
                        // meaningless for this scope
                        continue;
                    }

                    if (this.stolenBaseClassifier(runnerAdv[RunnerSafe.Type.key])) {
                        // base stolen
                        stolenBases++;
                    }

                    if (this.caughtStealingClassifier(runnerAdv[RunnerSafe.Type.key])) {
                        // base stolen
                        caughtStealing++;
                    }

                    // see https://baseball.fandom.com/wiki/Run
                    // TODO - consider maybe using RUNS property as well ?
                    if (
                        runnerAdv[RunnerSafe.TargetBase.key] === 4
                        && this.runClassifier(runnerAdv[RunnerSafe.Type.key])
                        && (
                            this.hasPitched(result, slice)
                            // avoid double calculations for home run, app shouldn't include this run in RunnerSafe, so it's just in case...
                            || !(isHomeRun && this.hasBatted(result, slice))
                        )
                    ) {
                        // scored a run
                        runs++;
                    }
                }
            }

            // check for home runs...
            if (
                isHomeRun
                && (
                    // for batting and pitching separately
                    this.hasBatted(result, slice)
                    || this.hasPitched(result, slice)
                )
            ) {
                // home run on inside the park home run
                runs++;
            }
        }

        if (runs > 0) {
            const runsParam = this.supportedParams[0];
            output[runsParam.key] = RankingStat.fromParameter(
                runsParam, this.numberFormatter.format(runs), runs
            );
        }
        if (stolenBases > 0) {
            const stolenBasesParam = this.supportedParams[1];
            output[stolenBasesParam.key] = RankingStat.fromParameter(
                stolenBasesParam, this.numberFormatter.format(stolenBases), stolenBases
            );
        }
        if (caughtStealing > 0) {
            const caughtStealingParams = this.supportedParams[2];
            output[caughtStealingParams.key] = RankingStat.fromParameter(
                caughtStealingParams, this.numberFormatter.format(caughtStealing), caughtStealing
            );
        }

        return output;
    }

    hasPitched(result, slice) {
        return (
            slice.scope.context === RankingScopeContext.Pitching.key
            && this.context === RankingScopeContext.Pitching.key
            && result[ResultDataNew.PitcherId.key] === slice.scope.tagId
        );
    }

    hasBatted(result, slice) {
        return (
            slice.scope.context === RankingScopeContext.Batting.key
            && this.context === RankingScopeContext.Batting.key
            && result[ResultDataNew.BatterId.key] === slice.scope.tagId
        );
    }
}

export default NewBaseRunningStatsCalculator;